import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { TodoPrimaryView } from '@/types/todo';

interface SelectionState {
  selectedTaskIds: Set<string>;
}

export type ListSortOption = 'manual' | 'date' | 'alphabetical';
export type ListGroupOption = 'none' | 'tag' | 'list';

interface ListSortPreference {
  sort: ListSortOption;
  group: ListGroupOption;
}

interface TodoViewStoreState {
  activeView: TodoPrimaryView;
  activeListId: string | null;
  activeProjectId: string | null;
  searchQuery: string;
  selectedTaskId: string | null;
  selection: SelectionState;
  lastSelectedTaskId: string | null;
  visibleTaskOrder: string[];
  listSortPreferences: Record<string, ListSortPreference>;
  setActiveView: (view: TodoPrimaryView, options?: { listId?: string | null; projectId?: string | null }) => void;
  setSearchQuery: (query: string) => void;
  setSelectedTaskId: (taskId: string | null) => void;
  toggleTaskSelection: (taskId: string) => void;
  setSelectionAnchor: (taskId: string | null) => void;
  selectRange: (orderedIds: string[], targetId: string) => void;
  selectAllVisible: (orderedIds: string[]) => void;
  clearSelection: () => void;
  setVisibleTaskOrder: (ids: string[]) => void;
  setListSortPreference: (listId: string, sort: ListSortOption, group: ListGroupOption) => void;
  getListSortPreference: (listId: string) => ListSortPreference;
  reset: () => void;
}

const DEFAULT_SORT_PREFERENCE: ListSortPreference = { sort: 'date', group: 'none' };

export const useTodoViewStore = create<TodoViewStoreState>()(
  conditionalDevtools(
    persist(
      (set, get) => ({
        activeView: 'inbox',
        activeListId: null,
        activeProjectId: null,
        searchQuery: '',
        selectedTaskId: null,
        selection: { selectedTaskIds: new Set() },
        lastSelectedTaskId: null,
        visibleTaskOrder: [],
        listSortPreferences: {},

        setActiveView: (view, options) =>
          set(() => ({
            activeView: view,
            activeListId: options?.listId ?? null,
            activeProjectId: options?.projectId ?? null,
            selectedTaskId: null,
            selection: { selectedTaskIds: new Set() },
            lastSelectedTaskId: null,
            visibleTaskOrder: [],
          })),

        setSearchQuery: (query) => set({ searchQuery: query }),

        setSelectedTaskId: (taskId) =>
          set({
            selectedTaskId: taskId,
            lastSelectedTaskId: taskId,
          }),

        toggleTaskSelection: (taskId) =>
          set((state) => {
            const next = new Set(state.selection.selectedTaskIds);
            if (next.has(taskId)) {
              next.delete(taskId);
            } else {
              next.add(taskId);
            }
            return { selection: { selectedTaskIds: next } };
          }),

        setSelectionAnchor: (taskId) => set({ lastSelectedTaskId: taskId }),

        selectRange: (orderedIds, targetId) =>
          set((state) => {
            const anchor = state.lastSelectedTaskId ?? state.selectedTaskId;
            if (!anchor) {
              return state;
            }
            const startIndex = orderedIds.indexOf(anchor);
            const endIndex = orderedIds.indexOf(targetId);
            if (startIndex === -1 || endIndex === -1) {
              return state;
            }
            const [start, end] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
            const rangeIds = orderedIds.slice(start, end + 1);
            const next = new Set(state.selection.selectedTaskIds);
            rangeIds.forEach((id) => next.add(id));
            return {
              selection: { selectedTaskIds: next },
              lastSelectedTaskId: targetId,
            };
          }),

        selectAllVisible: (orderedIds) =>
          set({
            selection: { selectedTaskIds: new Set(orderedIds) },
            lastSelectedTaskId: orderedIds.at(-1) ?? null,
          }),

        clearSelection: () =>
          set({
            selection: { selectedTaskIds: new Set() },
            lastSelectedTaskId: null,
          }),

        setVisibleTaskOrder: (ids) => set({ visibleTaskOrder: ids }),

        setListSortPreference: (listId, sort, group) =>
          set((state) => ({
            listSortPreferences: {
              ...state.listSortPreferences,
              [listId]: { sort, group },
            },
          })),

        getListSortPreference: (listId) => {
          return get().listSortPreferences[listId] ?? DEFAULT_SORT_PREFERENCE;
        },

        reset: () =>
          set({
            activeView: 'inbox',
            activeListId: null,
            activeProjectId: null,
            searchQuery: '',
            selectedTaskId: null,
            selection: { selectedTaskIds: new Set() },
            lastSelectedTaskId: null,
            visibleTaskOrder: [],
            listSortPreferences: {},
          }),
      }),
      {
        name: 'todo-view-store',
        version: 2,
        partialize: (state) => ({
          activeView: state.activeView,
          activeListId: state.activeListId,
          activeProjectId: state.activeProjectId,
          listSortPreferences: state.listSortPreferences,
        }),
      }
    ),
    { name: 'TodoViewStore' }
  )
);
